import React, { useState } from 'react';
import type { PfPatientFormRecord, PfPatientFormsBundle } from '../../patient-forms/types';
import {
  PF_STAGES,
  PF_STAGE_LABEL,
  PF_SYNC_LABEL,
  pfAssignmentLabel,
  pfAssignmentTone,
  pfFormStage,
  pfSyncTone,
} from '../../patient-forms/utils';
import type { PfStageState } from '../../patient-forms/utils';
import type { PatientRecord } from '../../patients/types';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { PatientProfileTabSectionHeader } from './PatientProfileTabSectionHeader';

export interface PatientFormsTabContentProps {
  patient: PatientRecord;
  bundle: PfPatientFormsBundle;
  /** Send the form link to the patient again, by text AND email. */
  onSendReminder?: (form: PfPatientFormRecord) => void;
  /** Open a read-only preview of the form. */
  onPreview?: (form: PfPatientFormRecord) => void;
}

const SYNC_ICON: Record<PfPatientFormRecord['syncStatus'], IconKind> = {
  not_started: 'clock',
  synced: 'check',
};

function StatusPill({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[99px] px-2.5 py-0.5 text-[11px] font-semibold"
      style={{
        color: `color-mix(in srgb, ${tone} 88%, #000)`,
        background: `color-mix(in srgb, ${tone} 12%, var(--cream-1))`,
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone }} />
      {children}
    </span>
  );
}

type NodeVisual = 'done' | 'active' | 'todo' | 'closed';

function nodeVisualAt(stage: PfStageState, i: number): NodeVisual {
  if (stage.terminal === 'incomplete') return i <= stage.index ? 'closed' : 'todo';
  if (stage.done) return 'done';
  if (i < stage.index) return 'done';
  if (i === stage.index) return 'active';
  return 'todo';
}

const NODE_STYLE: Record<
  NodeVisual,
  { dot: string; ring?: string; icon?: IconKind; iconColor?: string; label: string }
> = {
  done: { dot: 'var(--success)', icon: 'check', iconColor: '#fff', label: 'var(--ink-2)' },
  closed: { dot: 'var(--ink-4)', icon: 'check', iconColor: '#fff', label: 'var(--ink-3)' },
  active: {
    dot: 'color-mix(in srgb, var(--ai-3) 16%, var(--cream-1))',
    ring: 'var(--ai-3)',
    label: 'var(--ai-3)',
  },
  todo: { dot: 'var(--cream-2)', label: 'var(--ink-4)' },
};

function PipelineStepper({ form }: { form: PfPatientFormRecord }) {
  const stageState = pfFormStage(form);
  return (
    <div className="flex items-start">
      {PF_STAGES.map((stage, i) => {
        const v = nodeVisualAt(stageState, i);
        const s = NODE_STYLE[v];
        const prev = i > 0 ? nodeVisualAt(stageState, i - 1) : null;
        const prevFilled = prev === 'done' || prev === 'closed';
        return (
          <React.Fragment key={stage}>
            {i > 0 ? (
              <div
                className="mt-[13px] h-[2px] flex-1 rounded-full"
                style={{ background: prevFilled ? 'var(--success)' : 'var(--cream-2)' }}
                aria-hidden
              />
            ) : null}
            <div className="flex w-[64px] shrink-0 flex-col items-center gap-1.5">
              <div
                className="flex h-[26px] w-[26px] items-center justify-center rounded-full"
                style={{
                  background: s.dot,
                  boxShadow: s.ring ? `0 0 0 2px ${s.ring}` : undefined,
                }}
              >
                {s.icon ? (
                  <Icon kind={s.icon} size={13} stroke={2.4} style={{ color: s.iconColor }} />
                ) : v === 'active' ? (
                  <span className="h-2 w-2 rounded-full" style={{ background: 'var(--ai-3)' }} />
                ) : null}
              </div>
              <span
                className="text-center text-[10.5px] font-semibold leading-tight"
                style={{ color: s.label }}
              >
                {PF_STAGE_LABEL[stage]}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function LedgerItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="pc-eyebrow text-[9.5px] text-[var(--ink-4)]">{label}</div>
      <div className="pc-mono-tab mt-0.5 text-[11.5px] text-[var(--ink-1)]">{children}</div>
    </div>
  );
}

/** The two dates the front desk cares about: when it was sent, when staff closed it. */
function ConsentLedger({ form }: { form: PfPatientFormRecord }) {
  const { sentAt, closedAt } = form.timeline;
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-2.5">
      <LedgerItem label="Sent">
        {sentAt != null ? (
          <>
            {sentAt}
            {form.channel != null ? (
              <span className="text-[var(--ink-4)]"> · {form.channel}</span>
            ) : null}
          </>
        ) : (
          <span className="text-[var(--ink-4)]">—</span>
        )}
      </LedgerItem>
      <LedgerItem label="Closed">
        {closedAt != null ? (
          <>
            {closedAt}
            {form.reviewedBy != null ? (
              <span className="text-[var(--ink-3)]"> · by {form.reviewedBy}</span>
            ) : null}
          </>
        ) : (
          <span className="text-[var(--ink-4)]">Not closed yet</span>
        )}
      </LedgerItem>
    </div>
  );
}

function FormActions({
  form,
  onSendReminder,
  onPreview,
}: {
  form: PfPatientFormRecord;
  onSendReminder?: (form: PfPatientFormRecord) => void;
  onPreview?: (form: PfPatientFormRecord) => void;
}) {
  const [reminded, setReminded] = useState(false);

  const status = form.assignmentStatus;
  const notSent = status === 'not_sent';
  const canRemind =
    status === 'not_sent' ||
    status === 'pending' ||
    status === 'in_progress' ||
    status === 'submitted';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        iconLeft="eye"
        onClick={() => {
          onPreview?.(form);
        }}
      >
        Preview
      </Button>
      {canRemind ? (
        <Button
          variant="secondary"
          size="sm"
          iconLeft={reminded ? 'check' : 'send'}
          title="Send the form link to the patient by text and email"
          onClick={() => {
            onSendReminder?.(form);
            setReminded(true);
            window.setTimeout(() => {
              setReminded(false);
            }, 2500);
          }}
        >
          {reminded ? 'Sent by text + email' : notSent ? 'Send form' : 'Send reminder'}
        </Button>
      ) : null}
    </div>
  );
}

function PatientFormRow({
  form,
  onSendReminder,
  onPreview,
}: {
  form: PfPatientFormRecord;
  onSendReminder?: (form: PfPatientFormRecord) => void;
  onPreview?: (form: PfPatientFormRecord) => void;
}) {
  const notSent = form.assignmentStatus === 'not_sent';
  const showSync = form.syncStatus === 'synced';

  return (
    <div className="pc-rise rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-4 shadow-[var(--sh-1)]">
      <div className="flex items-start gap-4">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--ai-3)_10%,var(--cream-1))]"
          aria-hidden
        >
          <Icon kind="doc" size={18} stroke={1.8} style={{ color: 'var(--ai-3)' }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="m-0 text-[15px] font-semibold text-[var(--ink-0)]">{form.name}</h3>
            <StatusPill tone={pfAssignmentTone(form.assignmentStatus, form.syncStatus)}>
              {pfAssignmentLabel(form)}
            </StatusPill>
            {showSync ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold"
                style={{ color: pfSyncTone(form.syncStatus) }}
              >
                <Icon kind={SYNC_ICON[form.syncStatus]} size={12} stroke={2.2} />
                {PF_SYNC_LABEL[form.syncStatus]}
              </span>
            ) : null}
          </div>
          <p className="m-0 mt-1 text-[13px] text-[var(--ink-3)]">{form.desc}</p>
        </div>
      </div>

      {notSent ? (
        <div className="mt-3 rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] px-4 py-3 text-[12px] text-[var(--ink-4)]">
          Not sent to the patient yet — ELVA will send it on its trigger, or staff can send it now.
        </div>
      ) : (
        <>
          <div className="mt-4">
            <PipelineStepper form={form} />
          </div>
          <div className="mt-4 border-t border-[var(--cream-2)] pt-3">
            <ConsentLedger form={form} />
          </div>
        </>
      )}

      <div className="mt-3.5 border-t border-[var(--cream-2)] pt-3">
        <FormActions
          form={form}
          {...(onSendReminder ? { onSendReminder } : {})}
          {...(onPreview ? { onPreview } : {})}
        />
      </div>
    </div>
  );
}

const STATS: { key: keyof PfPatientFormsBundle['stats']; label: string; color: string }[] = [
  { key: 'completed', label: 'Synced', color: 'var(--success)' },
  { key: 'needsReview', label: 'Needs review', color: 'var(--gold)' },
  { key: 'total', label: 'Total', color: 'var(--ai-3)' },
];

export function PatientFormsTabContent({
  patient,
  bundle,
  onSendReminder,
  onPreview,
}: PatientFormsTabContentProps) {
  const { stats } = bundle;

  return (
    <div className="pc-rise mx-auto w-full max-w-[880px]">
      <PatientProfileTabSectionHeader
        eyebrow="Patient forms"
        title={patient.name}
        meta={`${String(stats.completed)}/${String(stats.total)} synced`}
        icon="doc"
        className="mb-6"
      />
      <div className="mb-4 flex flex-wrap gap-3">
        {STATS.map(({ key, label, color }) => (
          <div
            key={key}
            className={cn(
              'rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3',
              'min-w-[100px] shadow-[var(--sh-1)]',
            )}
          >
            <div className="pc-eyebrow mb-1 text-[var(--ink-3)]">{label}</div>
            <div
              className="font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em]"
              style={{ color }}
            >
              {stats[key]}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {bundle.forms.map((form) => (
          <PatientFormRow
            key={form.id}
            form={form}
            {...(onSendReminder ? { onSendReminder } : {})}
            {...(onPreview ? { onPreview } : {})}
          />
        ))}
      </div>
    </div>
  );
}
